import { useEffect, useRef, useState } from 'react';

const services = [
  {
    number: '01',
    title: '鑑寶 LAB',
    subtitle: '奢侈品箱包鑑定',
    tagline: '技術中繼站・爭議解決',
    description: '專精 Hermès, Chanel, LV。提供 Entrupy AI 儀器驗真 與 仿品證據信 (LOE)。',
    features: [
      '線上圖片初判 (避雷低仿) - $399 起',
      '證書查驗 (過濾假證) - $300',
      'ENTRUPY實物驗真 - $1999起 (糾紛處理)',
    ],
    buttonText: '啟動驗真',
    image: 'https://public.readdy.ai/ai/img_res/c089666d47c54be4f29f3514009c06a2.jpg',
  },
  {
    number: '02',
    title: '炅白 Jiong Bai',
    subtitle: '收藏級翡翠 & 彩寶裸石',
    tagline: '雙重認證・資產保值',
    description: '嚴選源頭 翡翠 (Jadeite) 與 紅藍寶石。堅持 不鑲嵌裸石 販售。',
    features: [
      '雙重證書策略：源頭證 + 台灣權威實驗室證書',
      '支持全國複檢，假一賠三',
    ],
    buttonText: '詢問今日報價',
    image: 'https://readdy.ai/api/search-image?query=Stunning%20high%20quality%20green%20jade%20jadeite%20gemstone%20glowing%20on%20pure%20black%20background%2C%20dramatic%20backlighting%20showing%20translucent%20internal%20structure%2C%20luxury%20jewelry%20photography%20with%20high%20contrast%20and%20sharp%20details%2C%20professional%20studio%20lighting&width=800&height=1000&seq=jade-glow-black-001&orientation=portrait',
  },
  {
    number: '03',
    title: '映白 Ying Bai',
    subtitle: '培育鑽石 & 天然水晶',
    tagline: '理性選物・規格化販售',
    description: '提供 培育鑽 (Lab-Grown) 與 天然水晶。拒絕模糊定價。',
    features: [
      '透明規格 (淨度/切工/材質)',
      '鑑定師親選，所見即所得',
    ],
    buttonText: '前往選購',
    image: 'https://readdy.ai/api/search-image?query=Sparkling%20brilliant%20cut%20diamond%20reflecting%20rainbow%20light%20on%20dark%20black%20surface%2C%20extreme%20macro%20photography%20showing%20facets%20and%20fire%2C%20luxury%20jewelry%20photography%20with%20dramatic%20lighting%20and%20high%20contrast%2C%20professional%20studio%20setup&width=800&height=1000&seq=diamond-sparkle-dark-001&orientation=portrait',
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="services" className="relative py-28 lg:py-36 bg-white overflow-hidden">
      {/* Background Text */}
      <div className="absolute top-8 right-0 pointer-events-none select-none overflow-hidden">
        <span className="block font-heading text-[10rem] lg:text-[18rem] leading-none text-black/[0.02] tracking-wider">
          SERVICES
        </span>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-16">
        {/* Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-black"></div>
              <span className="text-[11px] font-body font-medium tracking-[0.25em] text-neutral-500 uppercase">
                Three Core Services
              </span>
            </div>
            <h2 className="font-heading text-5xl lg:text-7xl text-black leading-[0.95] tracking-wide">
              三大核心業務
            </h2>
          </div>
          <p className="font-body text-[14px] text-neutral-600 leading-relaxed max-w-sm mt-6 lg:mt-0">
            專精名牌包、翡翠、寶石鑑定，為您的珍貴資產提供科學憑證。
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group relative bg-neutral-50 hover:bg-white border border-neutral-200 hover:border-black transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              {/* Image */}
              <div className="relative aspect-[4/5] overflow-hidden bg-black">
                <img
                  src={service.image}
                  alt={service.subtitle}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                {/* Number Badge */}
                <div className="absolute top-6 left-6 w-14 h-14 bg-white/95 backdrop-blur-sm flex items-center justify-center border border-black/10">
                  <span className="font-heading text-2xl text-black">{service.number}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Title */}
                <div className="mb-6">
                  <h3 className="font-heading text-3xl text-black tracking-wide mb-2">
                    {service.title}
                  </h3>
                  <p className="font-body text-[15px] text-black font-semibold mb-1">
                    {service.subtitle}
                  </p>
                  <p className="font-body text-[12px] text-neutral-500 tracking-wide">
                    {service.tagline}
                  </p>
                </div>

                {/* Description */}
                <p className="font-body text-[13px] text-neutral-700 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-line text-black/60 mt-0.5 flex-shrink-0"></i>
                      <span className="font-body text-[13px] text-neutral-800 leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <a
                  href="#appointment"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-black text-white font-body text-[13px] font-semibold tracking-wider hover:bg-neutral-800 transition-all duration-300 cursor-pointer whitespace-nowrap group/btn"
                >
                  {service.buttonText}
                  <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
